import { getMarketingConsent } from './consent';
import { captureFrontendException, observabilityEnabled } from './observability';

type AnalyticsParams = Record<string, string | number | boolean | null | undefined>;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
    dataLayer?: unknown[];
    fbq?: (...args: unknown[]) => void;
    _fbq?: (...args: unknown[]) => void;
  }
}

const normalizeEnvValue = (value: string | undefined): string =>
  (value || '').replace(/\\r|\\n|\r|\n/g, '').trim();

const GA4_ID = normalizeEnvValue(process.env.REACT_APP_GA4_MEASUREMENT_ID);
const ENABLED = Boolean(GA4_ID);
const META_PIXEL_ID = normalizeEnvValue(process.env.REACT_APP_META_PIXEL_ID);
const META_ENABLED = Boolean(META_PIXEL_ID);

const log = (...args: unknown[]) => {
  if (process.env.NODE_ENV === 'development') {
    // Keep analytics debug logs out of production console.
    // eslint-disable-next-line no-console
    console.log('[analytics]', ...args);
  }
};

const callFbq = (...args: unknown[]) => {
  if (typeof window === 'undefined') return;
  if (typeof window.fbq === 'function') {
    window.fbq(...args);
  }
};

const isGaDebugMode = (): boolean => {
  if ((process.env.REACT_APP_GA4_DEBUG_MODE || '').toLowerCase() === 'true') {
    return true;
  }
  if (typeof window === 'undefined') return false;

  const debugParam = new URLSearchParams(window.location.search).get('ga_debug');
  if (debugParam === '1' || debugParam?.toLowerCase() === 'true') {
    try {
      window.sessionStorage.setItem('st_ga_debug', '1');
    } catch {
      // Ignore storage failures; URL param still enables debug mode.
    }
    return true;
  }

  try {
    return window.sessionStorage.getItem('st_ga_debug') === '1';
  } catch {
    return false;
  }
};

const withGaDestination = (params: AnalyticsParams = {}): AnalyticsParams => {
  const nextParams: AnalyticsParams = {
    ...params,
    send_to: GA4_ID,
  };
  if (isGaDebugMode()) {
    nextParams.debug_mode = true;
  }
  return nextParams;
};

export const initAnalytics = (): void => {
  if (typeof window === 'undefined') return;

  // UTM capture is first-party attribution state and must work even when GA4
  // is disabled in local/test environments.
  captureUtms();

  if (!ENABLED) return;
  if (window.gtag) return;

  const script = document.createElement('script');
  script.async = true;
  script.src = `https://www.googletagmanager.com/gtag/js?id=${GA4_ID}`;
  document.head.appendChild(script);

  window.dataLayer = window.dataLayer || [];
  window.gtag = function gtag() {
    window.dataLayer?.push(arguments);
  };

  window.gtag('js', new Date());
  window.gtag('config', GA4_ID, { send_page_view: false });
  
  log('initialized', GA4_ID);
};

export const initMetaPixel = (): void => {
  if (typeof window === 'undefined') return;
  if (!META_PIXEL_ID) return;
  if (getMarketingConsent() !== 'granted') return;
  if (window.fbq) return;

  // Meta Pixel base script
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  (function (f: any, b: Document, e: string, v: string, n?: any, t?: HTMLScriptElement, s?: HTMLScriptElement) {
    if (f.fbq) return;
    n = f.fbq = function (...args: unknown[]) {
      if (n.callMethod) {
        n.callMethod.apply(n, args);
      } else {
        n.queue.push(args);
      }
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = true;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e) as HTMLScriptElement;
    t.async = true;
    t.src = v;
    s = b.getElementsByTagName(e)[0] as HTMLScriptElement;
    s.parentNode?.insertBefore(t, s);
  })(
    window,
    document,
    'script',
    'https://connect.facebook.net/en_US/fbevents.js'
  );

  callFbq('init', META_PIXEL_ID);
  callFbq('track', 'PageView');
  log('meta pixel initialized');
};

export const setUserId = (userId: string | number | null): void => {
  if (typeof window === 'undefined') return;
  if (ENABLED && window.gtag) {
    window.gtag('set', 'user_id', userId);
    log('setUserId', userId);
  }
};

export const trackEvent = (eventName: string, params: AnalyticsParams = {}): void => {
  if (typeof window === 'undefined') return;
  if (ENABLED && window.gtag) {
    window.gtag('event', eventName, withGaDestination(params));
  }
  if (getMarketingConsent() === 'granted') {
    callFbq('trackCustom', eventName, params);
  }
};

export const trackFirstOutputGenerated = (params: AnalyticsParams = {}): void => {
  const eventParams = {
    service_id: 'ai_marketing',
    participant_program: 'modu_startup',
    ...params,
  };
  trackEvent('first_output_generated', eventParams);
  trackEvent('first_output_created', eventParams);
};

export const trackDraftAdopted = (params: AnalyticsParams = {}): void => {
  trackEvent('draft_adopted', {
    service_id: 'ai_marketing',
    participant_program: 'modu_startup',
    ...params,
  });
};

export const trackPageView = (path?: string): void => {
  if (typeof window === 'undefined') return;
  const pagePath = path || `${window.location.pathname}${window.location.search}`;
  if (ENABLED && window.gtag) {
    window.gtag('event', 'page_view', withGaDestination({
      page_path: pagePath,
      page_title: document.title,
      page_location: window.location.href,
    }));
  }
  if (getMarketingConsent() === 'granted') {
    callFbq('track', 'PageView');
  }
};

export const startPageViewTracking = (): void => {
  if (typeof window === 'undefined') return;
  if (!ENABLED && !META_ENABLED) return;

  const capture = () => trackPageView();
  capture();

  const originalPushState = window.history.pushState;
  const originalReplaceState = window.history.replaceState;

  window.history.pushState = function pushState(...args) {
    originalPushState.apply(window.history, args);
    capture();
  };

  window.history.replaceState = function replaceState(...args) {
    originalReplaceState.apply(window.history, args);
    capture();
  };

  window.addEventListener('popstate', capture);
};

export const registerGlobalErrorTracking = (): void => {
  if (typeof window === 'undefined') return;
  if (!ENABLED && !META_ENABLED && !observabilityEnabled()) return;

  window.addEventListener('error', (event) => {
    captureFrontendException(event.error || new Error(event.message || 'unknown_error'), {
      source: event.filename || '',
      line: event.lineno || 0,
      column: event.colno || 0,
      fatal: true,
    });
    trackEvent('app_exception', {
      message: event.message || 'unknown_error',
      source: event.filename || '',
      line: event.lineno || 0,
      column: event.colno || 0,
      fatal: true,
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason = String(event.reason || 'unknown_rejection');
    captureFrontendException(event.reason || new Error(reason), {
      fatal: false,
    });
    trackEvent('app_exception', {
      message: reason.slice(0, 200),
      fatal: false,
    });
  });
};

/**
 * URL에서 UTM 파라미터를 추출하여 sessionStorage에 저장
 */
export const captureUtms = (): void => {
  if (typeof window === 'undefined') return;
  
  try {
    const urlParams = new URLSearchParams(window.location.search);
    const utms: Record<string, string> = {};
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];
    
    let hasUtm = false;
    utmKeys.forEach(key => {
      const value = urlParams.get(key);
      if (value) {
        utms[key] = value;
        hasUtm = true;
      }
    });
    
    if (hasUtm) {
      window.sessionStorage.setItem('st_utm_data', JSON.stringify(utms));
      log('captured utms', utms);
    }
  } catch (err) {
    log('failed to capture utms', err);
  }
};

/**
 * 저장된 UTM 데이터 반환
 */
export const getStoredUtms = (): Record<string, string> | null => {
  if (typeof window === 'undefined') return null;
  const stored = window.sessionStorage.getItem('st_utm_data');
  if (!stored) return null;
  try {
    return JSON.parse(stored);
  } catch {
    return null;
  }
};

export const analyticsEnabled = (): boolean => ENABLED;
